import { defineStore } from 'pinia'
import { RemovableRef, useLocalStorage } from '@vueuse/core'
import { NavigationItemParent } from '../../typings'
import { i18n } from '../../localization'
import {
    BadgeCheckIcon,
    BriefcaseIcon,
    CashIcon,
    CollectionIcon,
    CreditCardIcon,
    DocumentTextIcon,
    HomeIcon,
    PencilAltIcon,
    ReceiptTaxIcon,
    UserAddIcon,
    UserGroupIcon,
} from '@heroicons/vue/outline'

import {
    BadgeCheckIcon as BadgeCheckIconFilled,
    BriefcaseIcon as BriefcaseIconFilled,
    CashIcon as CashIconFilled,
    DocumentTextIcon as DocumentTextIconFilled,
    HomeIcon as HomeIconFilled,
    PencilAltIcon as PencilAltIconFilled,
    ReceiptTaxIcon as ReceiptTaxIconFilled,
    UserAddIcon as UserAddIconFilled,
    UserGroupIcon as UserGroupIconFilled,
} from '@heroicons/vue/solid'
import { getOrdersLink, getWalletLink } from '../useLinks'
import { getLegalAccountValidationSchema, handleDirtyState } from './useAccountFormLegal'
import { useLegalAccountDataState } from './useAccountDataLegal'
import { useForm } from 'vee-validate'

export const navigationItemsLegal: NavigationItemParent<LegalAccountPage>[] = [
    {
        name: i18n.global.t('account_settings.dashboard'),
        icon: HomeIcon,
        iconActive: HomeIconFilled,
        component: 'Dashboard',
    },
    {
        name: i18n.global.t('account_settings_legal.overview'),
        children: [
            {
                name: i18n.global.t('account_settings_legal.company_data'),
                icon: BriefcaseIcon,
                iconActive: BriefcaseIconFilled,
                component: 'Company Data',
            },
            {
                name: i18n.global.t('account_settings_legal.address_data'),
                icon: HomeIcon,
                iconActive: HomeIconFilled,
                component: 'Address Data',
            },
            {
                name: i18n.global.t('account_settings_legal.bank_data'),
                icon: CashIcon,
                iconActive: CashIconFilled,
                component: 'Bank Data',
            },
            {
                name: i18n.global.t('account_settings_legal.tax_data'),
                icon: ReceiptTaxIcon,
                iconActive: ReceiptTaxIconFilled,
                component: 'Tax Data',
            },
            {
                name: i18n.global.t('account_settings_legal.verification'),
                icon: BadgeCheckIcon,
                iconActive: BadgeCheckIconFilled,
                component: 'Verification',
            },
            {
                name: i18n.global.t('account_settings_legal.crypto_wallet'),
                icon: CreditCardIcon,
                iconActive: CreditCardIcon,
                component: 'Crypto Wallet',
            },
            {
                name: i18n.global.t('account_settings_legal.manager_area'),
                icon: UserAddIcon,
                iconActive: UserAddIconFilled,
                component: 'Manager',
            },
            {
                name: i18n.global.t('account_settings_legal.beneficial_owners'),
                icon: UserGroupIcon,
                iconActive: UserGroupIconFilled,
                component: 'Beneficial Owners',
            },
            {
                name: i18n.global.t('account_settings.compliance_profile'),
                icon: DocumentTextIcon,
                iconActive: DocumentTextIconFilled,
                component: 'Compliance Profile',
            },
            {
                name: i18n.global.t('account_settings.signatures'),
                icon: PencilAltIcon,
                iconActive: PencilAltIconFilled,
                component: 'Signatures',
            },
            {
                name: i18n.global.t('account_settings.membership_management'),
                icon: UserGroupIcon,
                iconActive: UserGroupIconFilled,
                component: 'Membership Management',
            },
        ],
    },
]

export const accountLinksLegal: NavigationItemParent[] = [
    {
        name: i18n.global.t('account_links.heading'),
        children: [
            {
                name: i18n.global.t('account_links.orders'),
                icon: CollectionIcon,
                href: getOrdersLink(),
            },
            {
                name: i18n.global.t('account_links.wallet'),
                icon: CreditCardIcon,
                href: getWalletLink(),
            },
        ],
    },
]

export type LegalAccountPage =
    | 'Dashboard'
    | 'Company Data'
    | 'Address Data'
    | 'Bank Data'
    | 'Tax Data'
    | 'Verification'
    | 'Crypto Wallet'
    | 'Manager'
    | 'Beneficial Owners'
    | 'Compliance Profile'
    | 'Signatures'
    | 'Membership Management'

export interface LegalAccountNavigationStore {
    page: LegalAccountPage | RemovableRef<LegalAccountPage>
    tab: string | null
}

export async function hasAccountLegalPageMissingFormFieldsForInvestment(page: LegalAccountPage) {
    const legalAccountState = useLegalAccountDataState()
    const legalAccount = legalAccountState.legalProfile

    const form = useForm({
        validationSchema: getLegalAccountValidationSchema(true)[page],
        initialValues: { legal_account: legalAccount },
        validateOnMount: true,
    })
    const validation = await form.validate()
    return !validation.valid
}

export const useLegalAccountNavigationState = defineStore('legal-account-navigation', {
    state: (): LegalAccountNavigationStore => {
        // @ts-ignore
        if (import.meta.env.PROD) {
            return {
                page: 'Dashboard',
                tab: null,
            }
        }

        return {
            page: useLocalStorage<LegalAccountPage>('legal-page', 'Dashboard'),
            tab: null,
        }
    },
})

export async function navigateToNextPageWithMissingFieldsForInvestmentLegal() {
    const store = useLegalAccountDataState()
    const formFieldPages = Object.keys(getLegalAccountValidationSchema(true)) as LegalAccountPage[]
    for (const accountPage of formFieldPages) {
        if (await hasAccountLegalPageMissingFormFieldsForInvestment(accountPage)) {
            await navigateTo(accountPage)
            return
        }
    }
    if (!store.isVerified) {
        await navigateTo('Verification')
    }
}

export const navigateTo = async function (page: LegalAccountPage, tab: string = null) {
    const store = useLegalAccountNavigationState()
    const confirmed = await handleDirtyState(useLegalAccountDataState())
    if (confirmed) {
        store.page = page
        store.tab = tab
    }
}

export function getHiddenLegalNavigationSections(): Array<string> {
    return window.condaHiddenLegalNavigationSections
}
